import { useEffect, useRef, useState } from "react";
import { useSidebar } from "../useSidebarProvider";
import RichTextEditor from "./editor/RichTextEditor";
import CustomModal from "./modal/Modal";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../redux/storeState/storeState";
import * as contentActions from "../../redux/content/ContentActions";
import toast from "react-hot-toast";

function ContenDetails() {
  const { collapsed } = useSidebar();
  const [editorValue, setEditorValue] = useState({ en: "", ar: "" });
  const [showModal, setShowModal] = useState(false);
  const [language, setLanguage] = useState<"en" | "ar">("en");
  // eslint-disable-next-line
  const [contentLoading, setContentLoading] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isUserChangedStyling, setIsUserChangedStyling] = useState(false);
  const handlePublish = () => {
    setShowModal(true);
  };
  const { contentForEdit } = useSelector((state: StoreState) => ({
    contentForEdit: state.content?.contentForEdit,
  }));

  const handleSave = () => {
    let dataToUpdate = {
      language: language,
      englishContent: editorValue?.en,
      arabicContent: editorValue?.ar,
      type: contentForEdit?.type,
      isActive: false,
    };
    dispatch(contentActions.updateContentById(id!, dataToUpdate)).then(() => {
      dispatch(
        contentActions.getAllContent({
          limit: 10,
          searchName: "",
          type: contentForEdit?.type,
        })
      );
    });
    setShowModal(false);
    dispatch(contentActions.setSelectedModule(contentForEdit?.type));
    navigate("/content");
  };
  const toastRef = useRef<string | null>(null);

  const hasContentOrFormattingChanges = (currentHtml: string | undefined | null, originalHtml: string | undefined | null): boolean => {
    if (!currentHtml && !originalHtml) return false;
    if (!currentHtml || !originalHtml) return true;

    const cleanHtml = (html: string): string => {
      return html?.replace(/dir="[^"]*"/g, "");
    };

    if (toastRef.current) {
      toast.dismiss(toastRef.current);
      toastRef.current = null;
    }
    const cleanedCurrent = cleanHtml(currentHtml);
    const cleanedOriginal = cleanHtml(originalHtml);

    if (cleanedCurrent === cleanedOriginal) return false;

    const isTextDifferent = hasTextChanges(currentHtml, originalHtml);
    if (isTextDifferent) return true;

    // if (!isTextDifferent && language === "ar") return false;
    if (!isTextDifferent && !isUserChangedStyling && language === "ar") return false;
    // If text is the same but HTML is different, formatting has changed
    return true;
  };

  // Keep your original function for text comparison
  const hasTextChanges = (currentHtml: string | undefined | null, originalHtml: string | undefined | null): boolean => {
    if (!currentHtml && !originalHtml) return false;
    if (!currentHtml || !originalHtml) return true;

    const getTextContent = (html: string): string => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent?.trim() || "";
    };
    return getTextContent(currentHtml) !== getTextContent(originalHtml);
  };

  const currentText = (editorValue[language] || "")?.trim();
  const originalText = (language === "en" ? contentForEdit?.englishContent : contentForEdit?.arabicContent || "")?.trim();
  // const hasChanges = currentText !== originalText;
  const hasChanges = hasContentOrFormattingChanges(currentText, originalText);
  const id = window?.location?.pathname?.split("/").pop();

  const dispatch: any = useDispatch();

  useEffect(() => {
    if (id) {
      setContentLoading(true);
      dispatch(contentActions.getContentById(id)).then(() => {
        setContentLoading(false);
      });
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (contentForEdit) {
      setEditorValue({
        en: contentForEdit.englishContent,
        ar: contentForEdit.arabicContent,
      });
    }
  }, [contentForEdit]);

  // const handleEditorChange = (newContent: string) => {
  //   setEditorValue({
  //     ...editorValue,
  //     [language]: newContent,
  //   });
  // };

  const [previousAttributes, setPreviousAttributes] = useState<any>({});
  const handleEditorChange = (content: any, delta: any, source: any, editor: any) => {
    if (source === "user") {
      delta.ops.forEach((op: any) => {
        if (op.attributes) {
          const currentAttrs = op.attributes;

          // Compare each style property individually
          Object.keys(currentAttrs).forEach((key) => {
            const prev = previousAttributes[key] ?? false;
            const curr = currentAttrs[key] ?? false;

            if (prev !== curr) {
              setIsUserChangedStyling(curr);
            } else {
              setIsUserChangedStyling(prev);
            }
          });

          // Update previous attributes state
          setPreviousAttributes(currentAttrs);
        }
      });
    }

    setEditorValue({
      ...editorValue,
      [language]: content,
    });
  };

  const navigate = useNavigate();
  const handleConfirmation = () => {
    let dataToUpdate = {
      language: language,
      isSaveOnly: hasChanges ? false : true,
      englishContent: editorValue?.en,
      arabicContent: editorValue?.ar,
      type: contentForEdit?.type,
      isActive: true,
      businessId: contentForEdit?.businessId,
    };
    dispatch(contentActions.updateContentById(id!, dataToUpdate)).then(() => {
      dispatch(
        contentActions.getAllContent({
          limit: 10,
          searchName: "",
          type: contentForEdit?.type,
        })
      );
    });
    setShowModal(false);
    dispatch(contentActions.setSelectedModule(contentForEdit?.type));
    navigate("/content");
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      dispatch(contentActions.setSelectedModule(contentForEdit?.type));
      navigate("/content");
    }
  };

  const handleChangeLanugage = (lang: any) => {
    const newToastId = toast.loading("Loading...");
    toastRef.current = newToastId;
    setTimeout(() => {
      setLanguage(lang);
    }, 200);
  };
  return (
    <div className="h-100" style={{ background: "rgb(242, 243, 248)" }}>
      <div
        className={`company-wrap content-details-wrapper ${collapsed ? "mr-5 ml-4" : "mr-5"}   m-0 pb-2`}
        style={{ paddingLeft: !collapsed ? "18.8rem" : "3.5rem ", overflow: "hidden" }}
      >
        <div className="pt-3 d-flex justify-content-between ">
          <div>
            <div className="title-text">{contentForEdit?.type === "terms" ? "Terms & Conditions" : "Privacy Policy"}</div>
            <div className="id-text py-1">ID {contentForEdit?.businessId}</div>
          </div>
          <div>
            <button className="back-button" onClick={handleBack}>
              Back
            </button>
          </div>
        </div>
        <div className="bg-white px-4 pt-2 mt-3 pb-3" style={{ minHeight: "80vh", borderRadius: "5.46px" }}>
          <RichTextEditor
            value={editorValue}
            // onChange={setEditorValue}
            onChange={handleEditorChange}
            placeholder="90333"
            handlePublish={handlePublish}
            handleSave={handleSave}
            language={language}
            changeLanguage={handleChangeLanugage}
            isPublished={contentForEdit?.isActive}
            hasChanges={hasChanges}
          />
        </div>
        <CustomModal
          isOpen={showModal}
          toggle={() => {
            setShowModal(!showModal);
          }}
          cancelText="CANCEL"
          confirmText="PUBLISH"
          onConfirm={handleConfirmation}
          title="Warning!"
          children={
            <>
              By clicking "Publish", you will update the {contentForEdit?.type === "terms" ? "Terms & Conditions" : "Privacy Policy"} across all
              supported languages. Are you sure you wish to continue?
            </>
          }
        />
        <CustomModal
          isOpen={showUnsavedChangesModal}
          toggle={() => {
            setShowUnsavedChangesModal(!showUnsavedChangesModal);
          }}
          cancelText="CANCEL"
          confirmText="CONTINUE"
          onConfirm={() => {
            setShowUnsavedChangesModal(false);
            navigate("/content");
          }}
          title="Unsaved changes"
          children={<>You have unsaved changes, are you sure you want to continue without saving?</>}
        />
      </div>
    </div>
  );
}

export default ContenDetails;
